
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { supabase, invokeFunction } from '@/integrations/supabase/client';
import { validatePhoneNumber, formatPhoneNumber } from '@/utils/phoneValidation';
import { Phone, Check, Loader2 } from 'lucide-react';

interface PhoneVerificationProps {
  phoneNumber: string;
  onPhoneChange: (phone: string) => void;
  onVerificationComplete: (verified: boolean) => void;
  isVerified: boolean;
  error?: string;
}

const PhoneVerification: React.FC<PhoneVerificationProps> = ({
  phoneNumber,
  onPhoneChange,
  onVerificationComplete,
  isVerified,
  error
}) => {
  const [verificationCode, setVerificationCode] = useState('');
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const { toast } = useToast();

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    onPhoneChange(formatted);
  };

  const sendVerificationCode = async () => {
    const validation = validatePhoneNumber(phoneNumber);
    if (!validation.isValid) {
      toast({
        title: "Invalid Phone Number",
        description: validation.error,
        variant: "destructive"
      });
      return;
    }

    setIsSending(true);
    try {
      const { data, error } = await invokeFunction(supabase, 'enroll-mfa', {
        body: {
          email: 'temp@example.com', // We'll use phone verification separately
          factorType: 'sms',
          phoneNumber: validation.formattedNumber
        }
      });

      if (error) throw error;

      setIsCodeSent(true);
      toast({
        title: "Verification Code Sent",
        description: "Please check your phone for the verification code."
      });
    } catch (error: any) {
      console.error('Error sending verification code:', error);
      toast({
        title: "Failed to Send Code",
        description: "Please try again or contact support.",
        variant: "destructive"
      });
    } finally {
      setIsSending(false);
    }
  };

  const verifyCode = async () => {
    if (!verificationCode.trim()) {
      toast({
        title: "Code Required",
        description: "Please enter the verification code.",
        variant: "destructive"
      });
      return;
    }

    setIsVerifying(true);
    try {
      const { data, error } = await invokeFunction(supabase, 'verify-mfa-v2', {
        body: {
          token: verificationCode,
          factorType: 'sms',
          phoneNumber: phoneNumber,
          action: 'verify'
        }
      });

      if (error) throw error;

      if (data.valid) {
        onVerificationComplete(true);
        toast({
          title: "Phone Verified",
          description: "Your phone number has been successfully verified."
        });
      } else {
        toast({
          title: "Invalid Code",
          description: "Please check the code and try again.",
          variant: "destructive"
        });
      }
    } catch (error: any) {
      console.error('Error verifying code:', error);
      toast({
        title: "Verification Failed",
        description: "Please try again or contact support.",
        variant: "destructive"
      });
    } finally {
      setIsVerifying(false);
    }
  };

  if (isVerified) {
    return (
      <div className="flex items-center space-x-2 p-3 bg-green-50 border border-green-200 rounded-md">
        <Check className="h-5 w-5 text-green-600" />
        <span className="text-green-700 font-medium">Phone number verified</span>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center space-x-2">
        <Phone className="h-4 w-4 text-gray-500" />
        <Input
          type="tel"
          value={phoneNumber}
          onChange={handlePhoneChange}
          placeholder="+1234567890"
          className={error ? 'border-red-500' : ''}
          disabled={isCodeSent || isSending}
        />
        {!isCodeSent && (
          <Button
            type="button"
            variant="outline"
            onClick={sendVerificationCode}
            disabled={isSending || !phoneNumber}
          >
            {isSending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Sending...
              </>
            ) : (
              'Send Code'
            )}
          </Button>
        )}
      </div>

      {error && (
        <p className="text-xs text-red-600">{error}</p>
      )}

      <p className="text-xs text-gray-600">
        We verify your phone number to comply with our security measures for future multi-factor authentication.
        Please ensure you have access to this phone number.
      </p>

      {isCodeSent && (
        <div className="flex items-center space-x-2">
          <Input
            type="text"
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value)}
            placeholder="Enter verification code"
            maxLength={6}
          />
          <Button
            type="button"
            onClick={verifyCode}
            disabled={isVerifying || !verificationCode}
          >
            {isVerifying ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Verifying...
              </>
            ) : (
              'Verify'
            )}
          </Button>
        </div>
      )}
    </div>
  );
};

export default PhoneVerification;
